/**
 * NeoCoreCondonacionesMoraPage — resguardo de condonaciones de mora.
 *
 * Módulo bajo Créditos. Lista TODO lo condonado (asociado, crédito, cuota, %,
 * monto, motivo, quién y cuándo) desde credito_condonacion_mora, con filtro de
 * fechas, KPIs de resumen y export a CSV. La condonación es siempre por % (100%
 * = total; parcial = un % de la mora pendiente).
 *
 * Backend: listar_condonaciones_mora(p_desde, p_hasta) · gate is_admin_or_contador.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowsClockwise, HandHeart, DownloadSimple, MagnifyingGlass, CaretLeft } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

type Row = {
  id: string
  fecha: string
  no_asociado: string
  nombre_completo: string
  credito_id: string
  credito_codigo: string | null
  cuota_numero: number | null
  pct: number
  monto_condonado: number
  mora_antes: number
  mora_despues: number
  motivo: string
  quien: string
}

const fmtQ = (n: number | null) =>
  'Q ' + Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtFechaHora = (iso: string) =>
  new Date(iso).toLocaleString('es-GT', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    timeZone: 'America/Guatemala',
  })

// Primer día del mes actual (TZ Guatemala) para el filtro por defecto.
function inicioMesGT(): string {
  const hoy = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' })
  return hoy.slice(0, 8) + '01'
}
function hoyGT(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' })
}

export function NeoCoreCondonacionesMoraPage() {
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(true)
  const [desde, setDesde] = useState<string>(inicioMesGT())
  const [hasta, setHasta] = useState<string>(hoyGT())
  const [todo, setTodo] = useState(false)
  const [condonarOpen, setCondonarOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase.rpc('listar_condonaciones_mora', {
      p_desde: todo ? null : desde,
      p_hasta: todo ? null : hasta,
    })
    if (error) { alert('Error: ' + error.message); setLoading(false); return }
    setRows((data as Row[]) ?? [])
    setLoading(false)
  }, [desde, hasta, todo])

  useEffect(() => { void load() }, [load])

  const kpis = useMemo(() => {
    const total = rows.reduce((s, r) => s + Number(r.monto_condonado), 0)
    const casos = rows.length
    const asociados = new Set(rows.map((r) => r.no_asociado)).size
    const totales = rows.filter((r) => Number(r.pct) >= 100).length
    return { total, casos, asociados, totales }
  }, [rows])

  const exportCsv = useCallback(() => {
    const head = ['Fecha', 'No. asociado', 'Asociado', 'Crédito', 'Cuota', '%', 'Monto condonado', 'Mora antes', 'Mora después', 'Motivo', 'Quién']
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`
    const lines = rows.map((r) => [
      fmtFechaHora(r.fecha), r.no_asociado, r.nombre_completo, r.credito_codigo ?? '',
      r.cuota_numero ?? '', Number(r.pct).toFixed(2), Number(r.monto_condonado).toFixed(2),
      Number(r.mora_antes).toFixed(2), Number(r.mora_despues).toFixed(2), r.motivo, r.quien,
    ].map(esc).join(','))
    const csv = [head.map(esc).join(','), ...lines].join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `condonaciones-mora-${todo ? 'historico' : desde + '_' + hasta}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }, [rows, desde, hasta, todo])

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 18, gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em' }}>Condonaciones de mora</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--muted-foreground)' }}>
            Resguardo de la mora condonada (por %). No toca capital ni interés — solo el recargo.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={exportCsv} className="btn btn-ghost" disabled={rows.length === 0}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <DownloadSimple size={14} weight="bold" /> Exportar CSV
          </button>
          <button onClick={() => void load()} className="btn btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ArrowsClockwise size={13} weight="bold" /> Refrescar
          </button>
          <button onClick={() => setCondonarOpen(true)} className="btn btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <HandHeart size={14} weight="bold" /> Condonar mora
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
        <Kpi label="Total condonado" value={fmtQ(kpis.total)} tone="danger" />
        <Kpi label="Condonaciones" value={String(kpis.casos)} sub={`${kpis.totales} al 100%`} />
        <Kpi label="Asociados" value={String(kpis.asociados)} />
      </div>

      {/* Filtro de fechas */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 12,
        padding: '12px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--muted-foreground)' }}>
          Desde
          <input type="date" value={desde} disabled={todo} onChange={(e) => setDesde(e.target.value)} style={inputStyle} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--muted-foreground)' }}>
          Hasta
          <input type="date" value={hasta} disabled={todo} onChange={(e) => setHasta(e.target.value)} style={inputStyle} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--ink-soft)', cursor: 'pointer' }}>
          <input type="checkbox" checked={todo} onChange={(e) => setTodo(e.target.checked)} /> Todo el historial
        </label>
      </div>

      {/* Tabla */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>
            <HandHeart size={28} weight="duotone" /><br />Sin condonaciones en este período.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--hairline)', background: 'var(--bg)' }}>
                <th style={th}>Fecha</th>
                <th style={th}>Asociado</th>
                <th style={th}>Crédito</th>
                <th style={{ ...th, textAlign: 'right' }}>%</th>
                <th style={{ ...th, textAlign: 'right' }}>Condonado</th>
                <th style={th}>Motivo</th>
                <th style={th}>Quién</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--line-soft)' }}>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12, color: 'var(--ink-soft, #334155)' }}>{fmtFechaHora(r.fecha)}</td>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{r.nombre_completo}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{r.no_asociado}</div>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 12 }}>{r.credito_codigo ?? '—'}</div>
                    {r.cuota_numero != null && <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>cuota {r.cuota_numero}</div>}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <span style={{
                      fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 99,
                      background: Number(r.pct) >= 100 ? 'rgba(163,45,45,0.10)' : 'rgba(217,119,6,0.12)',
                      color: Number(r.pct) >= 100 ? 'var(--danger, #a32d2d)' : 'var(--warning, #d97706)',
                    }}>{Number(r.pct).toFixed(0)}%</span>
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtQ(r.monto_condonado)}</td>
                  <td style={{ ...td, fontSize: 12, color: 'var(--ink-soft)', maxWidth: 340 }}>{r.motivo}</td>
                  <td style={{ ...td, fontSize: 12, color: 'var(--muted-foreground)', whiteSpace: 'nowrap' }}>{r.quien}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {condonarOpen && (
        <CondonarMoraFlow
          onClose={() => setCondonarOpen(false)}
          onDone={() => { setCondonarOpen(false); void load() }}
        />
      )}
    </div>
  )
}

// ============================================================
// Flujo · Condonar mora (elegir crédito en mora → %/total + motivo)
// ============================================================
type CredMora = {
  credito_id: string
  credito_codigo: string | null
  no_asociado: string
  nombre_completo: string
  mora_pendiente: number
  dias_atraso: number | null
}

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

function CondonarMoraFlow({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [creds, setCreds] = useState<CredMora[]>([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [sel, setSel] = useState<CredMora | null>(null)

  const [modo, setModo] = useState<'total' | 'parcial'>('total')
  const [tipoParcial, setTipoParcial] = useState<'pct' | 'monto'>('monto')
  const [valor, setValor] = useState('')
  const [motivo, setMotivo] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancel = false
    ;(async () => {
      const { data, error } = await supabase.rpc('creditos_con_mora_pendiente')
      if (cancel) return
      if (error) { alert('Error: ' + error.message); setLoading(false); return }
      setCreds((data as CredMora[]) ?? [])
      setLoading(false)
    })()
    return () => { cancel = true }
  }, [])

  const filtered = useMemo(() => {
    const t = norm(q.trim())
    if (!t) return creds
    return creds.filter((c) => norm(c.nombre_completo).includes(t) || c.no_asociado.includes(t) || (c.credito_codigo ?? '').includes(t))
  }, [creds, q])

  const moraPendiente = sel?.mora_pendiente ?? 0
  const pctRaw = !sel ? 0 : modo === 'total'
    ? 100
    : (Number(valor) > 0
        ? (tipoParcial === 'pct' ? Math.min(100, Number(valor)) : Math.min(100, (Number(valor) / moraPendiente) * 100))
        : 0)
  const montoAplicar = Math.round((moraPendiente * pctRaw) / 100 * 100) / 100

  const confirmar = async () => {
    if (!sel) return
    if (!motivo.trim()) { alert('El motivo es obligatorio (queda en el resguardo).'); return }
    if (pctRaw <= 0) { alert('Ingresá un monto o porcentaje válido.'); return }
    setBusy(true)
    const { data, error } = await supabase.rpc('condonar_mora_credito', {
      p_credito_id: sel.credito_id, p_pct: pctRaw, p_motivo: motivo.trim(),
    })
    setBusy(false)
    if (error) { alert('Error: ' + error.message); return }
    const res = data as { condonado_total?: number } | null
    alert(`Mora condonada: ${fmtQ(res?.condonado_total ?? montoAplicar)}. Queda registrado.`)
    onDone()
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,18,25,0.55)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(480px, 100%)', maxHeight: '88vh', background: '#fff', borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.24)' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--hairline)', display: 'flex', alignItems: 'center', gap: 10 }}>
          {sel && (
            <button onClick={() => setSel(null)} className="btn btn-ghost" style={{ padding: '4px 8px' }}>
              <CaretLeft size={14} weight="bold" />
            </button>
          )}
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Condonar mora</h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>
              {sel ? `${sel.nombre_completo} · ${sel.no_asociado}` : 'Elegí el crédito en mora'}
            </p>
          </div>
        </div>

        {!sel ? (
          <>
            <div style={{ padding: '12px 22px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg)', borderRadius: 8, padding: '6px 10px', border: '1px solid var(--hairline)' }}>
                <MagnifyingGlass size={14} weight="bold" color="var(--muted-foreground)" />
                <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Asociado, número o código…"
                  style={{ flex: 1, border: 0, background: 'transparent', outline: 'none', fontSize: 13, fontFamily: 'inherit' }} />
              </div>
            </div>
            <div style={{ padding: '12px 22px 20px', overflowY: 'auto' }}>
              {loading ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>
              ) : filtered.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted-foreground)', fontSize: 13 }}>
                  {creds.length === 0 ? 'No hay créditos con mora pendiente.' : 'Sin coincidencias.'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {filtered.map((c) => (
                    <button key={c.credito_id} onClick={() => { setSel(c); setModo('total'); setValor(''); setMotivo('') }}
                      style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 12px', border: '1px solid var(--hairline)', borderRadius: 10 }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{c.nombre_completo}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
                          {c.no_asociado} · {c.credito_codigo ?? '—'}{c.dias_atraso ? ` · ${c.dias_atraso}d atraso` : ''}
                        </div>
                      </div>
                      <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--danger, #a32d2d)', whiteSpace: 'nowrap' }}>{fmtQ(c.mora_pendiente)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 12px', background: 'rgba(163,45,45,0.05)', border: '1px solid rgba(163,45,45,0.18)', borderRadius: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--muted-foreground)', fontWeight: 700 }}>Mora pendiente</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--danger, #a32d2d)' }}>{fmtQ(moraPendiente)}</span>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                {(['total', 'parcial'] as const).map((m) => (
                  <button key={m} onClick={() => setModo(m)} style={{
                    all: 'unset', cursor: 'pointer', flex: 1, textAlign: 'center', padding: '10px 0', borderRadius: 8,
                    fontSize: 13, fontWeight: 700, border: '1px solid',
                    borderColor: modo === m ? 'var(--brand)' : 'var(--hairline)',
                    background: modo === m ? 'var(--brand-50)' : 'transparent',
                    color: modo === m ? 'var(--brand-700)' : 'var(--ink-soft)',
                  }}>{m === 'total' ? 'Condonar todo' : 'Parcial'}</button>
                ))}
              </div>

              {modo === 'parcial' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {(['monto', 'pct'] as const).map((t) => (
                      <button key={t} onClick={() => setTipoParcial(t)} style={{
                        all: 'unset', cursor: 'pointer', padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 700,
                        background: tipoParcial === t ? 'var(--brand)' : 'var(--n-100)', color: tipoParcial === t ? '#fff' : 'var(--muted-foreground)',
                      }}>{t === 'monto' ? 'Por monto (Q)' : 'Por %'}</button>
                    ))}
                  </div>
                  <input type="number" inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)}
                    placeholder={tipoParcial === 'monto' ? 'Monto a condonar (Q)' : 'Porcentaje (%)'}
                    style={{ padding: '9px 10px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 13 }} />
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-soft)' }}>
                    Se condonará <strong>{fmtQ(montoAplicar)}</strong> · quedará pendiente {fmtQ(Math.max(0, moraPendiente - montoAplicar))}
                  </p>
                </div>
              )}

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)' }}>
                Motivo (obligatorio · queda en el resguardo contable)
                <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3}
                  placeholder="Ej. Asociado con intención de pago; se apoya con la mora del atraso por…"
                  style={{ padding: '9px 10px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} />
              </label>

              <p style={{ margin: 0, fontSize: 11, color: 'var(--muted-foreground)' }}>
                No borra el atraso ni toca capital/interés. El asociado sigue en mora hasta que pague la cuota; solo se le quita el recargo.
              </p>
            </div>
            <div style={{ padding: '14px 22px', borderTop: '1px solid var(--hairline)', display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }} disabled={busy}>Cancelar</button>
              <button className="btn btn-primary" onClick={confirmar} style={{ flex: 1 }} disabled={busy || !motivo.trim() || pctRaw <= 0}>
                {busy ? 'Condonando…' : `Condonar ${fmtQ(montoAplicar)}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'danger' }) {
  const color = tone === 'danger' ? 'var(--danger, #a32d2d)' : 'var(--muted-foreground)'
  const border = tone === 'danger' ? 'rgba(163,45,45,0.30)' : 'var(--hairline)'
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid', borderColor: border, borderRadius: 12, padding: '14px 16px' }}>
      <p style={{ margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: 1.1, textTransform: 'uppercase', color }}>{label}</p>
      <p style={{ margin: '6px 0 0', fontSize: 21, fontWeight: 800, color: 'var(--ink)', fontFeatureSettings: '"tnum"' }}>{value}</p>
      {sub && <p style={{ margin: '2px 0 0', fontSize: 11, fontWeight: 600, color: 'var(--muted-foreground)' }}>{sub}</p>}
    </div>
  )
}

const th: React.CSSProperties = { textAlign: 'left', padding: '10px 14px', fontSize: 10, fontWeight: 800, color: 'var(--n-600)', textTransform: 'uppercase', letterSpacing: 1.2 }
const td: React.CSSProperties = { padding: '12px 14px' }
const inputStyle: React.CSSProperties = { padding: '7px 9px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }

export default NeoCoreCondonacionesMoraPage
